import { componentCls } from "@faire/slate/lib/tracking";
import { SkeletonBase } from "@faire/slate/private/loading-skeleton/SkeletonBase";
import {
  SkeletonVariant,
  ISkeletonWebProps,
  ISkeletonPropsConstraints,
} from "@faire/slate/private/loading-skeleton/type";
import { getSkeletonVars } from "@faire/slate/private/loading-skeleton/utils";

type IProps = {
  /**
   * The diameter of the avatar.
   *
   * @default 64
   */
  diameter?: number;

  /**
   * The height of the skeleton.
   */
  height?: number;

  /**
   * The size of the image.
   *
   * @default 240
   */
  size?: number;

  /**
   * The variant of the skeleton. Each variant defines different props to customize the skeleton.
   * - `canvas`: custom width and height, default is 100%
   * - `square`: size of the square, default is 240px
   * - `circle`: diameter of the circle, default is 64px
   * - `titleText`: width of the title text, default is 136px, height is fixed 32px
   * - `paragraphText`: width of the paragraph text, default is 240px, height is fixed 20px
   *
   * @default canvas
   */
  variant?: SkeletonVariant;

  /**
   * The width of the skeleton.
   */
  width?: number;
} & ISkeletonWebProps;

/**
 * A loading spinner represents an unspecified loading time.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * @trackcomponent
 * @version 2.0.0
 */
export const LoadingSkeleton: React.FC<ISkeletonPropsConstraints<IProps>> = ({
  diameter,
  height,
  size,
  variant = "canvas",
  width,
  ...props
}) => {
  const skeletonVars = getSkeletonVars(variant, {
    width,
    height,
    diameter,
    size,
  });

  return (
    <SkeletonBase
      className={componentCls()}
      style={skeletonVars}
      {...props}
      role="status"
    />
  );
};
